/**
 * Workspace URL slugs (IMM-322). A workspace's URL segment is
 * `<slugified-name>-<slugId>`, where `slugId` is an immutable suffix of the
 * workspace UUID. The human prefix is purely cosmetic and is recomputed whenever
 * the workspace is renamed; resolution keys ONLY on the `slugId` (the part after
 * the final hyphen), so a link stays valid forever even after a rename, and a
 * stale prefix is canonicalised by the client on entry. Shared by the backend
 * (which composes the slug in `/auth/me`) and the web app (which parses it).
 */

/**
 * Lower-cased, diacritics stripped (ü→u), non-alphanumerics collapsed to single
 * hyphens, trimmed. A name that slugifies to nothing (e.g. all punctuation or a
 * non-latin script) yields the empty string — callers fall back to the bare
 * `slugId`.
 */
export function slugify(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Cap the human prefix so URLs stay sane even for very long workspace names.
const MAX_PREFIX_LENGTH = 40;

/**
 * The immutable identifier derived from a workspace UUID: the trailing hex of
 * the id (the high-entropy random tail of a v7 UUID — NOT the time-ordered
 * prefix). `length` is extended only on the rare collision, by the generator.
 */
export function slugIdFromWorkspaceId(workspaceId: string, length = 8): string {
  const hex = workspaceId.replace(/-/g, '');

  return hex.slice(-length);
}

/** Compose the full URL slug for a workspace from its name and immutable slugId. */
export function composeWorkspaceSlug(name: string, slugId: string): string {
  const prefix = slugify(name).slice(0, MAX_PREFIX_LENGTH).replace(/-+$/g, '');

  return prefix === '' ? slugId : `${prefix}-${slugId}`;
}

/**
 * Extract the immutable slugId from a full slug — everything after the final
 * hyphen (the slugId is hex, so it never contains one, even though the human
 * prefix may). A bare slugId (no prefix) returns itself.
 */
export function slugIdOf(slug: string): string {
  const lastDash = slug.lastIndexOf('-');

  return lastDash === -1 ? slug : slug.slice(lastDash + 1);
}
